import { ReactNode, useMemo, useState } from 'react';

import { Modal } from '@/shared/ui';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';

import { getFullName } from '../../../helpers';
import { MedicationUpdatePopup } from '../medication-update-popup';
import { PackagingViewEnum } from '@/shared/types/enums/packaging';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import { useAppDispatch } from '@/shared/hooks/store';
import { CustomersController } from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { IPharmacy } from '@/shared/types/interfaces/pharmacy';
import { IProviderAddress } from '@/shared/types/interfaces/provider';

export interface IMedicationCardProps {
    id?: string;
    medication: ITransferPrescriptions;
    showActions?: boolean;
    onDelete?: () => void;
}

const FormatAddress = ({ addressLine1, addressLine2, city, state, zip }: IPharmacy | IProviderAddress, name: string) => {
    return (
        <div className="flex max-w-full flex-col pb-0.5 pb-3 font-atkinsonHyperlegible text-15 capitalize sm:text-17 md:text-lg">
            <span className="overflow-hidden text-ellipsis font-atkinsonBold">{name}</span>

            <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {addressLine1}&nbsp;{addressLine2}
            </span>
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                {city},&nbsp;{state}&nbsp;{zip}
            </span>
        </div>
    );
};

export const MedicationCard = ({ id, medication, showActions = true, onDelete }: IMedicationCardProps) => {
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [isEditPopupOpened, setIsEditPopupOpened] = useState(false);
    const [deleteModalShown, setDeleteModalShown] = useState(false);

    const toggleEditPopup = () => setIsEditPopupOpened(!isEditPopupOpened);

    const medicationInfoList = useMemo(() => {
        if (medication.prescribable.otc && (!medication.provider || !medication.fromPharmacy)) {
            return [PackagingViewEnum[medication.packaging], medication.quantity, medication.direction];
        }

        const providerInfo = medication.provider ? [FormatAddress(medication.provider.address, getFullName(medication.provider))] : [];

        const pharmacyInfo = medication.fromPharmacy ? [FormatAddress(medication.fromPharmacy, medication.fromPharmacy.name)] : [];

        return [...providerInfo, ...pharmacyInfo].filter(Boolean);
    }, [medication]);

    const deleteMedication = async () => {
        setIsLoading(true);
        try {
            const res = await dispatch(CustomersController.removeTransferPrescription(medication));
            if ('error' in res && (res.error.message || '').includes('Rejected')) {
                toast.error('Failed to remove medication');
            } else {
                onDelete?.();
            }
        } catch (error) {
            toast.error('Failed to remove medication');
        }
        setIsLoading(false);
        setDeleteModalShown(false);
    };

    return (
        <>
            <div
                id={id}
                data-testid={`medication-card-${id}`}
                className="relative flex w-full max-w-full items-start rounded-2xl border-1 border-black p-5 lg:py-[1.4375rem] lg:pl-[1.5625rem] lg:pr-5"
            >
                <div className="w-[100%] sm:flex-auto">
                    <div className="flex w-full flex-row items-center justify-between pb-3">
                        <h4 data-testid="medication-card-prescribable-name" className="w-[80%] font-atkinsonBold text-17 uppercase text-black sm:text-21 md:text-lg">
                            {medication.prescribable.name}
                        </h4>
                        {showActions && (
                            <div>
                                <i
                                    onClick={() => setDeleteModalShown(true)}
                                    className="icon-bin mini-max:right-1 flex h-[35px] w-[35px] flex-auto cursor-pointer items-center justify-center rounded-full bg-mint-400 text-base text-primary-800"
                                    data-testid="medication-card-delete-icon"
                                />
                            </div>
                        )}
                    </div>
                    <div className="border-b-1" />
                    <div className="gap-0. mt-[10px] flex flex-col">
                        {medicationInfoList.map((info: string | ReactNode, ind) => (
                            <span data-testid={`medication-card-info-${ind}`} key={ind} className="font-atkinsonHyperlegible text-sm uppercase lg:text-base">
                                {info}
                            </span>
                        ))}
                    </div>
                </div>
                <ConfirmModal
                    open={!!deleteModalShown}
                    onCancel={() => setDeleteModalShown(false)}
                    onConfirm={deleteMedication}
                    title="Are you sure you want to remove this medication?"
                    confirmText="Yes, Remove"
                    cancelText="Cancel Removal"
                    loading={isLoading}
                >
                    <MedicationCard medication={medication} showActions={false} />
                </ConfirmModal>
            </div>

            <Modal dataTestId="medication-list-card-update" open={isEditPopupOpened} onClose={toggleEditPopup} className="!px-7 !py-10">
                <MedicationUpdatePopup onClose={toggleEditPopup} medication={medication} />
            </Modal>
        </>
    );
};
